// src/components/UserMeet/User-Meet-Private/WaitingRoomScreen.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Clock,
  Users,
  Shield,
  X,
  Loader2,
  Video,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { socketService } from "@/Services/socketService";
import { getStoredUserId } from "@/utils/getUserId";

export default function WaitingRoomScreen({
  joinCode,
  meetingId,
  participantId,
  meetingTitle,
  message,
  token,
  onAdmitted,
  onDenied,
  onCancel,
}) {
  const navigate = useNavigate();
  const hasRegisteredRef = useRef(false);

  const [status, setStatus] = useState("waiting");
  const [dots, setDots] = useState("");
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  /* ─── Animated dots ─── */
  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? "" : prev + "."));
    }, 600);
    return () => clearInterval(interval);
  }, []);

  /* ─── Elapsed timer ─── */
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  /* ─── Connect socket, register in waiting room, and attach all listeners ───
   *
   * Root-cause fix: previously the three separate effects (register, admitted,
   * denied) all ran immediately on mount and called socketService.on() /
   * socketService.emit() before the socket was initialised, producing
   * "socket not initialized" warnings and silently dropping the
   * register-usermeet-waiting-user emit.  The server therefore never fired
   * usermeet-admission-request to the host.
   *
   * Fix: we call socketService.connect(token, onConnected) here.  Everything
   * — listeners AND the registration emit — happens inside onConnected so the
   * socket is guaranteed to exist.  If the socket is already connected (e.g.
   * a reconnect scenario) we attach immediately.
   * ─────────────────────────────────────────────────────────────────────────*/
  useEffect(() => {
    if (!meetingId) return;

    const myUserId = getStoredUserId();

    console.log(`🔍 WaitingRoomScreen userId resolved: "${myUserId}"`);
    console.log(`🔍 WaitingRoomScreen participantId: "${participantId}"`);

    if (!myUserId) {
      console.error(
        "❌ WaitingRoomScreen: Could not resolve userId. " +
          "Socket registration will fail.",
      );
    }

    // ── Event handlers ────────────────────────────────────────────────────────

    const handleAdmitted = (payload) => {
      console.log("✅ Admitted to meeting:", payload);
      if (String(payload?.meetingId) !== String(meetingId)) return;

      setStatus("admitted");
      toast.success("You've been admitted to the meeting!");

      const uid = getStoredUserId();
      socketService.emit("register-usermeet-user", { userId: uid, meetingId });
      console.log(
        `✅ Registered as full participant | meetingId: ${meetingId} | userId: ${uid}`,
      );

      setTimeout(() => {
        onAdmitted?.({
          meetingId: payload.meetingId,
          livekitToken: payload.livekitToken,
          livekitIdentity: payload.livekitIdentity,
          livekitServerUrl: payload.livekitServerUrl,
          livekitRoomName: payload.livekitRoomName,
        });
      }, 1500);
    };

    const handleDenied = (payload) => {
      console.log("❌ Denied from meeting:", payload);
      if (String(payload?.meetingId) !== String(meetingId)) return;

      setStatus("denied");
      toast.error(
        payload?.message || "The host did not admit you to this meeting.",
      );

      setTimeout(() => {
        onDenied?.(payload);
      }, 3000);
    };

    const handleMeetingEnded = (payload) => {
      if (String(payload?.meetingId) !== String(meetingId)) return;
      toast.error("This meeting has ended.");
      setStatus("denied");
      setTimeout(() => {
        navigate("/user-meet", { replace: true });
      }, 2000);
    };

    // ── Attach listeners and emit registration ────────────────────────────────
    // Both hyphen and underscore variants are covered in case the server differs.

    const attachListenersAndRegister = () => {
      // Attach admitted / denied / ended listeners
      socketService.on("usermeet-admitted", handleAdmitted);
      socketService.on("usermeet_admitted", handleAdmitted);
      socketService.on("usermeet-denied", handleDenied);
      socketService.on("usermeet_denied", handleDenied);
      socketService.on("usermeet-ended", handleMeetingEnded);

      // Register in the waiting-room socket room (emit once only)
      if (!hasRegisteredRef.current) {
        const emitted = socketService.emit("register-usermeet-waiting-user", {
          userId: myUserId,
          meetingId,
          participantId,
        });
        if (emitted) {
          hasRegisteredRef.current = true;
          console.log(
            `✅ Registered in waiting room | meetingId: ${meetingId} | userId: ${myUserId} | participantId: ${participantId}`,
          );
        }
      }
    };

    if (socketService.isConnected()) {
      // Socket is already live — attach immediately
      attachListenersAndRegister();
    } else {
      // Socket is not yet connected — connect now, attach inside the callback.
      // This is the common case when the guest arrives from JoinUserMeetingPage
      // which does not pre-connect the socket.
      console.log("🔌 WaitingRoomScreen: socket not ready, connecting…");
      socketService.connect(token, () => {
        console.log("✅ WaitingRoomScreen: socket connected, registering…");
        attachListenersAndRegister();
      });
    }

    return () => {
      socketService.off("usermeet-admitted", handleAdmitted);
      socketService.off("usermeet_admitted", handleAdmitted);
      socketService.off("usermeet-denied", handleDenied);
      socketService.off("usermeet_denied", handleDenied);
      socketService.off("usermeet-ended", handleMeetingEnded);
    };
  }, [meetingId, participantId, token, onAdmitted, onDenied, navigate]);

  /* ─── Cancel ─── */
  const handleCancel = useCallback(() => {
    onCancel?.();
  }, [onCancel]);

  // ─── ADMITTED STATE ───
  if (status === "admitted") {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{
          background: "linear-gradient(180deg, #061412 0%, #0A1D1A 100%)",
        }}
      >
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-3xl bg-green-500/15 border border-green-500/30 flex items-center justify-center mx-auto mb-6 animate-bounce">
            <CheckCircle2 size={40} className="text-green-400" />
          </div>

          <h2
            className="text-3xl font-light tracking-tight text-white mb-3"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            You're <span className="italic text-green-400">in!</span>
          </h2>

          <p className="text-white/50 text-sm mb-6">
            The host has admitted you. Connecting to the meeting now…
          </p>

          <div className="w-10 h-10 border-2 border-green-400 border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  // ─── DENIED STATE ───
  if (status === "denied") {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{
          background: "linear-gradient(180deg, #061412 0%, #0A1D1A 100%)",
        }}
      >
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-3xl bg-red-500/15 border border-red-500/30 flex items-center justify-center mx-auto mb-6">
            <XCircle size={40} className="text-red-400" />
          </div>

          <h2
            className="text-3xl font-light tracking-tight text-white mb-3"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Request <span className="italic text-red-400">declined</span>
          </h2>

          <p className="text-white/50 text-sm mb-8">
            The host did not admit you to this meeting. You'll be redirected
            shortly.
          </p>

          <button
            onClick={() => navigate("/user-meet", { replace: true })}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-white/[0.12] text-white/70 text-sm font-medium hover:border-white/30 hover:text-white hover:bg-white/[0.04] transition-all"
          >
            Back to Meetings
          </button>
        </div>
      </div>
    );
  }

  // ─── WAITING STATE ───
  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{
        background: "linear-gradient(180deg, #061412 0%, #0A1D1A 100%)",
      }}
    >
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 50% 40%, rgba(198,168,124,0.06) 0%, transparent 60%)",
        }}
      />

      <div className="relative max-w-lg w-full text-center">
        <div className="mb-8">
          <div className="relative inline-flex items-center justify-center w-24 h-24 mx-auto">
            <div className="absolute inset-0 rounded-3xl border-2 border-[#C6A87C]/20 animate-ping" />
            <div className="absolute inset-1 rounded-3xl border border-[#C6A87C]/10 animate-pulse" />
            <div className="relative w-20 h-20 rounded-3xl bg-[#C6A87C]/10 border border-[#C6A87C]/25 flex items-center justify-center">
              <Clock size={36} className="text-[#C6A87C]" />
            </div>
          </div>
        </div>

        <h2
          className="text-3xl md:text-4xl font-light tracking-tight text-white mb-3"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Waiting for the <span className="italic text-[#C6A87C]">host</span>
          {dots}
        </h2>

        <p className="text-white/50 text-sm mb-2 max-w-sm mx-auto">
          {message ||
            "Your join request has been sent. The host will admit you shortly."}
        </p>

        {meetingTitle && (
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.04] border border-white/[0.08] mt-2 mb-8">
            <Video size={14} className="text-[#C6A87C]" />
            <span className="text-sm text-white/60">{meetingTitle}</span>
          </div>
        )}

        <div
          className="rounded-2xl border border-white/[0.08] overflow-hidden mb-8"
          style={{ background: "rgba(255,255,255,0.03)" }}
        >
          <div className="px-6 py-5 border-b border-white/[0.06]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#C6A87C]/10 border border-[#C6A87C]/20 flex items-center justify-center">
                  <Loader2 size={18} className="text-[#C6A87C] animate-spin" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-white/80">
                    Waiting to be admitted
                  </p>
                  <p className="text-xs text-white/40">
                    The host will let you in soon
                  </p>
                </div>
              </div>

              <div className="text-right">
                <p className="text-lg font-mono text-[#C6A87C] font-medium">
                  {formatTime(elapsedSeconds)}
                </p>
                <p className="text-[10px] text-white/30 uppercase tracking-widest">
                  Waiting
                </p>
              </div>
            </div>
          </div>

          <div className="px-6 py-5">
            <div className="space-y-3">
              {[
                {
                  icon: Shield,
                  label: "Secure waiting room",
                  desc: "The host controls who enters the meeting",
                  color: "text-green-400",
                  bg: "bg-green-500/10 border-green-500/20",
                },
                {
                  icon: Users,
                  label: "Host notified",
                  desc: "The host has been notified of your request",
                  color: "text-blue-400",
                  bg: "bg-blue-500/10 border-blue-500/20",
                },
                {
                  icon: Video,
                  label: "Ready to connect",
                  desc: "You'll be connected instantly when admitted",
                  color: "text-[#C6A87C]",
                  bg: "bg-[#C6A87C]/10 border-[#C6A87C]/20",
                },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.label}
                    className={`flex items-start gap-3 p-3 rounded-xl border ${item.bg}`}
                  >
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${item.bg}`}
                    >
                      <Icon size={14} className={item.color} />
                    </div>
                    <div className="text-left">
                      <p className={`text-sm font-medium ${item.color}`}>
                        {item.label}
                      </p>
                      <p className="text-xs text-white/40 mt-0.5">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <button
          onClick={handleCancel}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-white/[0.1] text-white/50 text-sm font-medium hover:border-red-500/30 hover:text-red-400 hover:bg-red-500/[0.04] transition-all group"
        >
          <X
            size={16}
            className="group-hover:rotate-90 transition-transform duration-200"
          />
          Leave waiting room
        </button>

        <p className="mt-6 text-xs text-white/20 font-mono">{joinCode}</p>
      </div>
    </div>
  );
}